import {
    Date_toDateString,
    Date_toDateTimeString,
    Date_toTimeString,
    Date_withTimeString
} from "../../../common/dateUtils";
import { CodeStatus, PermitCategory } from "../../../common/enums";
import { FormState_clearErrors, FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import BaseUserInfo from "../../BaseUserInfo";
import CoordinationPermit from "../../permits/CoordinationPermit";
import CoordinationPermitRequest from "../../requests/CoordinationPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_toPerson,
    PersonFormElement_populateErrorMessages
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type CoordinationPermitFormState = {
    description: FormElement<string>;
    positions: PositionPickerRow[];
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    referenceNr: FormElement<string>;
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    entrepreneur: FormElement<string>;
    entrepreneurContactPersonEmail: FormElement<string>;
    entrepreneurContactPerson: PersonFormElement;
    company: FormElement<string>;
    companyContactPerson: PersonFormElement;
    companyContactPersonEmail: FormElement<string>;
    ghabInformsCompanies: FormElement<string>;
    senderInformsCompanies: FormElement<string>;
    informPerformer: FormElement<boolean>;
    workCloseToNeighbours: FormElement<boolean>;
    permitCategory: FormElement<PermitCategory>;
};

export default CoordinationPermitFormState;

export function CoordinationPermitFormState_default(baseUserInfo: BaseUserInfo): CoordinationPermitFormState {
    const defaultFormState: CoordinationPermitFormState = {
        company: formElement(baseUserInfo.companyName),
        companyContactPersonEmail: formElement(baseUserInfo.emailAddress),
        description: formElement(""),
        endDate: formElement(new Date()),
        entrepreneur: formElement(""),
        entrepreneurContactPerson: PersonFormElement_default(),
        entrepreneurContactPersonEmail: formElement(""),
        ghabInformsCompanies: formElement(""),
        informPerformer: formElement(false),
        positions: [PositionPickerRow_default()],
        referenceNr: formElement(""),
        senderInformsCompanies: formElement(""),
        startDate: formElement(new Date()),
        workCloseToNeighbours: formElement(false),
        permitCategory: formElement(PermitCategory.HotWorkCategoryOne),
        companyContactPerson: PersonFormElement_default(),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        remindMe: formElement(false),
        remindMeDate: formElement(new Date()),
    };

    defaultFormState.companyContactPerson.name = baseUserInfo.fullName;
    defaultFormState.companyContactPerson.phoneNumber = baseUserInfo.phoneNumber;

    return defaultFormState;
}

export function CoordinationPermitFormState_toRequest(
    formState: CoordinationPermitFormState
): CoordinationPermitRequest {
    const endDate = Date_withTimeString(formState.startDate.value, Date_toTimeString(formState.endDate.value));
    const endDateString = Date_toDateTimeString(endDate);

    const request: CoordinationPermitRequest = {
        company: formState.company.value,
        companyContactPersonEmail: formState.companyContactPersonEmail.value,
        description: formState.description.value,
        endDate: endDateString,
        entrepreneur: formState.entrepreneur.value,
        entrepreneurContactPerson: PersonFormElement_toPerson(formState.entrepreneurContactPerson),
        entrepreneurContactPersonEmail: formState.entrepreneurContactPersonEmail.value,
        ghabInformsCompanies: formState.ghabInformsCompanies.value,
        informPerformer: formState.informPerformer.value,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        referenceNr: formState.referenceNr.value,
        senderInformsCompanies: formState.senderInformsCompanies.value,
        startDate: Date_toDateTimeString(formState.startDate.value),
        workCloseToNeighbours: formState.workCloseToNeighbours.value,
        permitCategory: formState.permitCategory.value,
        companyContactPerson: PersonFormElement_toPerson(formState.companyContactPerson)
    };

    return request;
}

export function CoordinationPermitFormState_populateErrorMessages(
    formState: CoordinationPermitFormState,
    errors: Errors
): CoordinationPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const entrepreneurContactPersonErrorKeys = errorKeys.filter((e) => e.startsWith("EntrepreneurContactPerson."));
    const entrepreneurContactPersonErrors = Object.fromEntries(
        entrepreneurContactPersonErrorKeys.map((k) => [String_removePrefix(k, "EntrepreneurContactPerson."), errors[k]])
    );

    const companyContactPersonErrorKeys = errorKeys.filter((e) => e.startsWith("CompanyContactPerson."));
    const companyContactPersonErrors = Object.fromEntries(
        companyContactPersonErrorKeys.map((k) => [String_removePrefix(k, "CompanyContactPerson."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);
    updatedFormState.entrepreneurContactPerson = PersonFormElement_populateErrorMessages(
        formState.entrepreneurContactPerson,
        entrepreneurContactPersonErrors
    );

    updatedFormState.companyContactPerson = PersonFormElement_populateErrorMessages(
        formState.companyContactPerson,
        companyContactPersonErrors
    );

    return updatedFormState;
}
